import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  LinearProgress,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import {
  Search,
  Refresh,
  AutorenewOutlined,
  SwapHoriz,
  Cancel as CancelIcon,
  CheckCircle,
  Schedule,
  Warning,
} from "@mui/icons-material";
import axios from "axios";
import logger from "../../utils/logger";

const Subscriptions = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [actionDialog, setActionDialog] = useState(false);
  const [actionType, setActionType] = useState("");
  const [newPlanId, setNewPlanId] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    expired: 0,
    expiring_soon: 0,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem("superAdminToken");
    const config = { headers: { Authorization: `Bearer ${token}` } };

    try {
      const [subsResponse, plansResponse] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_API_URL}/super-admin/subscriptions`,
          config
        ),
        axios.get(`${process.env.REACT_APP_API_URL}/super-admin/plans`, config),
      ]);

      const subsData = subsResponse.data.data;
      setSubscriptions(subsData);
      setPlans(plansResponse.data.data);

      // Calculate stats
      const now = new Date();
      const sevenDaysFromNow = new Date(
        now.getTime() + 7 * 24 * 60 * 60 * 1000
      );

      setStats({
        total: subsData.length,
        active: subsData.filter((s) => s.status === "active").length,
        expired: subsData.filter((s) => s.status === "expired").length,
        expiring_soon: subsData.filter((s) => {
          const endDate = new Date(s.end_date);
          return (
            s.status === "active" &&
            endDate <= sevenDaysFromNow &&
            endDate > now
          );
        }).length,
      });
    } catch (error) {
      logger.error("Error fetching data:", error);
      showSnackbar("فشل تحميل البيانات", "error");
    } finally {
      setLoading(false);
    }
  };

  const openActionDialog = (subscription, action) => {
    setSelectedSubscription(subscription);
    setActionType(action);
    if (action === "change-plan") {
      setNewPlanId("");
    }
    setActionDialog(true);
  };

  const handleAction = async () => {
    const token = localStorage.getItem("superAdminToken");
    const config = { headers: { Authorization: `Bearer ${token}` } };

    try {
      switch (actionType) {
        case "renew":
          await axios.post(
            `${process.env.REACT_APP_API_URL}/super-admin/subscriptions/${selectedSubscription.id}/renew`,
            {},
            config
          );
          showSnackbar("تم تجديد الاشتراك بنجاح", "success");
          break;

        case "change-plan":
          if (!newPlanId) {
            showSnackbar("يرجى اختيار الخطة الجديدة", "warning");
            return;
          }
          await axios.put(
            `${process.env.REACT_APP_API_URL}/super-admin/subscriptions/${selectedSubscription.id}/change-plan`,
            { new_plan_id: newPlanId },
            config
          );
          showSnackbar("تم تغيير الخطة بنجاح", "success");
          break;

        case "cancel":
          await axios.put(
            `${process.env.REACT_APP_API_URL}/super-admin/subscriptions/${selectedSubscription.id}/cancel`,
            {},
            config
          );
          showSnackbar("تم إلغاء الاشتراك بنجاح", "success");
          break;

        case "approve":
          await axios.put(
            `${process.env.REACT_APP_API_URL}/super-admin/tenants/${selectedSubscription.tenant_id}/approve-subscription`,
            {},
            config
          );
          showSnackbar("تم تأكيد الدفع وتفعيل الاشتراك بنجاح", "success");
          break;

        default:
          break;
      }
      fetchData();
      setActionDialog(false);
    } catch (error) {
      logger.error("Error performing action:", error);
      showSnackbar(
        error.response?.data?.message || "فشل تنفيذ العملية",
        "error"
      );
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "success";
      case "pending_payment":
        return "info";
      case "expired":
        return "error";
      case "cancelled":
        return "default";
      default:
        return "default";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "active":
        return "نشط";
      case "pending_payment":
        return "بانتظار التأكيد";
      case "expired":
        return "منتهي";
      case "cancelled":
        return "ملغي";
      default:
        return status;
    }
  };

  const getDaysRemaining = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const filteredSubscriptions = subscriptions.filter((sub) => {
    const matchesSearch =
      sub.tenant_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.subdomain?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter =
      filterStatus === "all" ||
      sub.status === filterStatus ||
      (filterStatus === "expiring" &&
        sub.status === "active" &&
        getDaysRemaining(sub.end_date) <= 7 &&
        getDaysRemaining(sub.end_date) > 0);

    return matchesSearch && matchesFilter;
  });

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" fontWeight="bold">
          إدارة الاشتراكات
        </Typography>
        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchData}>
          تحديث
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                إجمالي الاشتراكات
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                الاشتراكات النشطة
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {stats.active}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                منتهية قريباً
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="warning.main">
                {stats.expiring_soon}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                الاشتراكات المنتهية
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="error.main">
                {stats.expired}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              placeholder="البحث في الاشتراكات..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>تصفية حسب الحالة</InputLabel>
              <Select
                value={filterStatus}
                label="تصفية حسب الحالة"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="all">الكل</MenuItem>
                <MenuItem value="active">نشط</MenuItem>
                <MenuItem value="expired">منتهي</MenuItem>
                <MenuItem value="cancelled">ملغي</MenuItem>
                <MenuItem value="expiring">منتهي قريباً</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      {loading ? (
        <LinearProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>المستأجر</strong>
                </TableCell>
                <TableCell>
                  <strong>الخطة</strong>
                </TableCell>
                <TableCell>
                  <strong>تاريخ البداية</strong>
                </TableCell>
                <TableCell>
                  <strong>تاريخ الانتهاء</strong>
                </TableCell>
                <TableCell>
                  <strong>الأيام المتبقية</strong>
                </TableCell>
                <TableCell>
                  <strong>الحالة</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>الإجراءات</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSubscriptions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary" py={3}>
                      لا توجد اشتراكات
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredSubscriptions.map((sub) => {
                  const daysRemaining = getDaysRemaining(sub.end_date);
                  const isExpiringSoon =
                    sub.status === "active" &&
                    daysRemaining <= 7 &&
                    daysRemaining > 0;

                  return (
                    <TableRow key={sub.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {sub.tenant_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {sub.subdomain}
                        </Typography>
                      </TableCell>
                      <TableCell>{sub.plan_name}</TableCell>
                      <TableCell>
                        {new Date(sub.start_date).toLocaleDateString("ar-IQ")}
                      </TableCell>
                      <TableCell>
                        {new Date(sub.end_date).toLocaleDateString("ar-IQ")}
                      </TableCell>
                      <TableCell>
                        {sub.status === "active" ? (
                          <Chip
                            label={`${daysRemaining} يوم`}
                            size="small"
                            color={isExpiringSoon ? "warning" : "default"}
                            icon={isExpiringSoon ? <Warning /> : <Schedule />}
                          />
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusText(sub.status)}
                          color={getStatusColor(sub.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {sub.status === "active" ? (
                          <>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => openActionDialog(sub, "renew")}
                              title="تجديد"
                            >
                              <AutorenewOutlined />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() =>
                                openActionDialog(sub, "change-plan")
                              }
                              title="تغيير الخطة"
                            >
                              <SwapHoriz />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => openActionDialog(sub, "cancel")}
                              title="إلغاء"
                            >
                              <CancelIcon />
                            </IconButton>
                          </>
                        ) : sub.status === "expired" ? (
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => openActionDialog(sub, "renew")}
                            title="تجديد"
                          >
                            <CheckCircle />
                          </IconButton>
                        ) : sub.status === "pending_payment" ? (
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => openActionDialog(sub, "approve")}
                            title="تأكيد وتفعيل"
                          >
                            <CheckCircle />
                          </IconButton>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            لا توجد إجراءات
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Action Dialog */}
      <Dialog
        open={actionDialog}
        onClose={() => setActionDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {actionType === "renew" && "تجديد الاشتراك"}
          {actionType === "change-plan" && "تغيير الخطة"}
          {actionType === "cancel" && "إلغاء الاشتراك"}
          {actionType === "approve" && "تأكيد الدفع وتفعيل الاشتراك"}
        </DialogTitle>
        <DialogContent>
          {selectedSubscription && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>المستأجر:</strong> {selectedSubscription.tenant_name}
                  <br />
                  <strong>الخطة الحالية:</strong>{" "}
                  {selectedSubscription.plan_name}
                </Typography>
              </Alert>

              {actionType === "renew" && (
                <Typography>
                  سيتم تجديد الاشتراك لمدة {selectedSubscription.duration_days}{" "}
                  يوم إضافية بسعر {selectedSubscription.price} دينار عراقي.
                </Typography>
              )}

              {actionType === "change-plan" && (
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel>اختر الخطة الجديدة</InputLabel>
                  <Select
                    value={newPlanId}
                    label="اختر الخطة الجديدة"
                    onChange={(e) => setNewPlanId(e.target.value)}
                  >
                    {plans
                      .filter(
                        (p) =>
                          p.id !== selectedSubscription.plan_id && p.is_active
                      )
                      .map((plan) => (
                        <MenuItem key={plan.id} value={plan.id}>
                          {plan.name} - {plan.price} IQD
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}

              {actionType === "cancel" && (
                <Alert severity="warning">
                  <Typography>
                    هل أنت متأكد من إلغاء هذا الاشتراك؟ سيتم تعطيل الوصول
                    للمستأجر فوراً.
                  </Typography>
                </Alert>
              )}

              {actionType === "approve" && (
                <Alert severity="success">
                  <Typography>
                    هل أنت متأكد من تأكيد استلام الدفعة وتفعيل الاشتراك؟ 
                    سيتم تفعيل وصول المستأجر إلى النظام فوراً.
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog(false)}>إلغاء</Button>
          <Button
            onClick={handleAction}
            variant="contained"
            color={actionType === "cancel" ? "error" : "primary"}
          >
            تأكيد
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Subscriptions;
